/**
 * Wanneer welke bron opnieuw wordt opgehaald.
 *
 * ------------------------------------------------------------------
 * Aanleiding, gemeten op 11 september tegen productie en vastgelegd in
 * docs/performance-collaboration.md:
 *
 *   /api/map      p50  9,78 s
 *   /api/board    p50  0,63 s
 *   /api/stats    p50  0,44 s   — maar 9,36 s terwijl er een kaart liep
 *
 * Een kaartverzoek houdt de hele server bezig zolang het duurt. En sinds
 * `watchPosition` hing álles — bord, kaart, statistieken, twee ranglijsten en
 * de 30-daagse patronen — aan hetzelfde positieanker van 150 meter. Bij
 * 100 km/u schuift dat anker elke 5,4 seconden op, terwijl één kaartverzoek
 * 9,8 seconden loopt. Ze overlapten dus permanent: één rijdende gebruiker
 * hield de server onafgebroken bezig en blokkeerde daarmee zijn eigen bord.
 *
 * Vandaar dit bestand. Vijf bronnen, vijf klokken, en per bron de vraag hoe
 * vers hij werkelijk hoort te zijn:
 *
 *   BORD          wat er vóór je ligt. Beweegt met je mee, dus aan het anker.
 *   KAART         wat er om je heen staat. Elke vijftien seconden, maar met
 *                 een straal van 25 km in plaats van de standaard 75 — dat is
 *                 wat hem van 12 seconden naar 1,2 bracht. Zie KAART_MS.
 *   STATISTIEKEN  landelijk. Verandert niet in een minuut.
 *   RANGLIJSTEN   idem, en nog trager.
 *   PATRONEN      dertig dagen geschiedenis. Die zijn morgen hetzelfde.
 *
 * De kaart wordt met opzet niet volledig van je locatie losgemaakt — dan zou
 * je na een uur rijden nog naar het gebied van je vertrekpunt kijken. Hij
 * krijgt een eigen anker. Dat is de aanscherping van Codex, en die is terecht.
 *
 * Zuivere functies, zodat het tempo te toetsen is zonder auto en zonder
 * netwerk. Dat is precies wat er ontbrak: deze regressie was niet uit de code
 * te lezen zonder er een rijsnelheid bij te halen.
 */

const M_PER_DEG_LAT = 111320.0;

/** Het anker van het bord: fijn, want het bord gaat over de volgende kilometer. */
export const BORD_M = 150;
export const BORD_MS = 30000;

/**
 * De kaart: elke vijftien seconden, en een straal van vijfentwintig kilometer.
 *
 * Die twee horen bij elkaar en zijn samen gemeten. Van de weg gevraagd: de
 * kaart mag vaker vers zijn dan de twee minuten die hier eerst stonden. Dat
 * kon niet zolang één kaartverzoek 9,8 seconden serverwerk kostte — elke
 * vijftien seconden verversen zou betekenen dat de server tweederde van de
 * tijd bezig is met één rijdende gebruiker, en intussen ook diens eigen bord
 * ophoudt.
 *
 * Maar /api/map neemt `radius_m` aan, en de standaard is 75 kilometer. Gemeten
 * op 11 september tegen productie:
 *
 *     75 km (standaard)   12,04 s   1890 pins
 *     25 km                1,24 s    379 pins
 *     15 km                1,23 s    167 pins
 *
 * Tien keer sneller, en de reden is dezelfde kwadratische lus die het
 * onderzoek al aanwees: `cluster_official` krijgt 379 documenten in plaats van
 * 5000. Vijfentwintig kilometer straal is vijftig kilometer breed, ruim meer
 * dan wat een telefoonscherm op zoomniveau 12 toont.
 *
 * Daarmee is vijftien seconden niet alleen mogelijk maar ook goedkoper dan de
 * twee minuten hiervoor: 240 verzoeken per uur van 1,24 s is 5 procent van de
 * tijd, tegen 50 verzoeken van 9,8 s — 14 procent. Vaker vers én lichter.
 *
 * De afstandsdrempel blijft staan voor het geval de tijd niet leidt: een
 * sprong in positie (een tunnel uit, een passagier die de app opent) hoort
 * meteen een verse kaart te geven.
 */
export const KAART_M = 2000;
export const KAART_MS = 15000;

/**
 * Hoe ver de kaart om je heen kijkt.
 *
 * De server staat standaard op 75 km en dat is de hele reden dat een
 * kaartverzoek twaalf seconden duurde. Dit is stap 2 van de afspraak in
 * docs/performance-collaboration.md, in zijn goedkoopste vorm: de client
 * vraagt gewoon minder.
 */
export const KAART_STRAAL_M = 25000;

/** En de drie die niets met je positie te maken hebben. */
export const STATS_MS = 300000;
export const RANGLIJST_MS = 600000;
export const PATROON_MS = 1800000;

/**
 * Hoe ver een kaartgebied mag verschuiven voordat het een ander gebied is.
 *
 * Los van KAART_M omdat de patronen een eigen, veel ruimere straal vragen
 * (35 km); daar is twee kilometer verschuiving helemaal niets.
 */
export const PATROON_M = 10000;

export function afstandM(aLat, aLon, bLat, bLon) {
  const mPerLon = M_PER_DEG_LAT * Math.cos((aLat * Math.PI) / 180);
  const dx = (bLon - aLon) * mPerLon;
  const dy = (bLat - aLat) * M_PER_DEG_LAT;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Of deze bron opnieuw opgehaald moet worden.
 *
 * `vorige` is wat er de laatste keer gehaald is: `{lat, lon, atMs}`, of null
 * als er nog nooit iets gehaald is. `nu` is de huidige positie plus klok.
 *
 * `meters` op null betekent: deze bron trekt zich niets van je positie aan.
 * Dat is geen bijzonder geval maar het normale geval voor drie van de vijf.
 */
export function moetOphalen(vorige, nu, { meters, ms }) {
  if (!vorige) return true;
  if (nu.atMs - vorige.atMs >= ms) return true;
  if (meters == null) return false;
  if (vorige.lat == null || nu.lat == null) return true;
  return afstandM(vorige.lat, vorige.lon, nu.lat, nu.lon) >= meters;
}

/** De vijf bronnen met hun eigen tempo, op één plek zodat ze te vergelijken zijn. */
export const TEMPO = {
  bord:      { meters: BORD_M,    ms: BORD_MS },
  kaart:     { meters: KAART_M,   ms: KAART_MS },
  stats:     { meters: null,      ms: STATS_MS },
  ranglijst: { meters: null,      ms: RANGLIJST_MS },
  patronen:  { meters: PATROON_M, ms: PATROON_MS },
};

/**
 * Hoeveel verzoeken een rit van een uur oplevert, per bron.
 *
 * Bestaat om de regressie te kunnen tóetsen in plaats van te beschrijven. De
 * oude situatie is één regel: alles op het bordanker, dus vijf keer het
 * bordaantal plus evenzoveel kaarten.
 */
export function verzoekenPerUur(kmh, tempo) {
  const meterPerUur = kmh * 1000;
  const uurMs = 3600000;
  const doorAfstand = tempo.meters == null ? 0 : Math.floor(meterPerUur / tempo.meters);
  const doorTijd = Math.floor(uurMs / tempo.ms);
  return Math.max(doorAfstand, doorTijd);
}
